<template>
  <Teleport to="body">
    <transition name="fade">
      <!-- Backdrop dismissal is supplementary; the modal has a keyboard-operable close button. -->
      <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
      <div v-if="modelValue" class="alert-review-modal__backdrop" data-qa="alert-review-modal-backdrop"
        @mousedown="close" />
    </transition>
    <transition name="panel-fade">
      <div v-if="modelValue" class="alert-review-modal" data-qa="alert-review-modal" role="dialog" aria-modal="true"
      aria-labelledby="alert-review-modal-title" @mousedown.stop>
      <div class="alert-review-modal__header">
        <div class="alert-review-modal__title-row">
          <h2 id="alert-review-modal-title" class="alert-review-modal__title">
            Alerts
          </h2>
          <span v-if="alertsStore.unreadAlertsCount > 0" class="alert-review-modal__badge"
            data-qa="alert-review-modal-count">
            {{ alertsStore.unreadAlertsCount }}
          </span>
        </div>
        <button class="alert-review-modal__close" aria-label="Close alerts" data-qa="alert-review-modal-close"
        @click="close">
        <img src="@/assets/icons/lightbox-cross-small.svg" alt="" aria-hidden="true" />
      </button>
    </div>
  
    <div class="alert-review-modal__body">
          <AlertsBody mode="full" @alert-selected="close" />
        </div>
      </div>
    </transition>
  </Teleport>
</template>

<script setup lang="ts">
import { computed } from 'vue';

import AlertsBody from '@/components/alerts-body/AlertsBody.vue';
import { useAlertsStore } from '@/stores';
import { useScrollLock } from '@/utils/useScrollLock';

interface Props {
  modelValue?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: boolean): void;
}>();

const alertsStore = useAlertsStore();

const isOpen = computed(() => props.modelValue);

useScrollLock(isOpen);

const close = () => {
  emit('update:modelValue', false);
};
</script>

<style scoped lang="scss">
.alert-review-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 102;
  display: flex;
  flex-direction: column;
  background: $brand-primary-100-white;
  overflow: hidden;

  @include new-ms {
    top: 50%;
    left: 50%;
    right: auto;
    bottom: auto;
    transform: translate(-50%, -50%);
    width: 520px;
    max-height: 90vh;
    border-radius: 16px;
    box-shadow: 0 8px 40px $black-rgb-25;
  }

  &__backdrop {
    position: fixed;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 101;
    background-color: $black-rgb-40;
  }

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 24px;
    flex-shrink: 0;
    background-color: $brand-secondary-100-light-blue;
  }

  &__title-row {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__title {
    color: $brand-primary-100-navy;
    font-size: remc(20px);
    font-family: "Museo Slab";
    line-height: remc(28px);
    font-weight: 600;
    margin: 0;
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 11px;
    background: $brand-secondary-100-red;
    color: $brand-primary-100-white;
    font-size: remc(12px);
    font-weight: 700;
    line-height: 1;
  }

  &__close {
    display: flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    cursor: pointer;
    padding: 4px;
    color: $brand-primary-100-navy;
  }

  &__body {
    padding-top: 20px;
    flex: 1;
    overflow-y: auto;
  }

  &__report-insight-widget {
    padding: 16px 16px 0px 16px;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.panel-fade-enter-active,
.panel-fade-leave-active {
  transition: opacity 0.15s ease;
}

.panel-fade-enter-from,
.panel-fade-leave-to {
  opacity: 0;
}
</style>
